import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import SaveCalculationDialog from '@/components/SaveCalculationDialog';
import { 
  ArrowLeft, 
  DollarSign,
  Percent,
  Home,
  PiggyBank,
  TrendingUp,
  Info,
  Download,
  Building2,
  Wallet,
  Calculator,
  CreditCard,
  ShieldCheck,
  HelpCircle,
  Save
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const AffordabilityCalculator = () => {
  const navigate = useNavigate();
  
  // Income & Debt
  const [annualIncome, setAnnualIncome] = useState(85000);
  const [monthlyDebts, setMonthlyDebts] = useState(500);
  
  // Down Payment
  const [downPayment, setDownPayment] = useState(50000);
  
  // Loan Details
  const [loanTerm, setLoanTerm] = useState(30);
  const [interestRate, setInterestRate] = useState(6.5);
  
  // Taxes & Insurance (as dollar amounts per year)
  const [propertyTax, setPropertyTax] = useState(5000);
  const [homeInsurance, setHomeInsurance] = useState(1500);
  const [monthlyHoa, setMonthlyHoa] = useState(0);
  
  // Assumptions
  const [maxDti, setMaxDti] = useState(43);
  const [maxHousingRatio, setMaxHousingRatio] = useState(28);
  
  // Results
  const [results, setResults] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  // Calculate affordability
  useEffect(() => {
    calculateAffordability();
  }, [annualIncome, monthlyDebts, downPayment, loanTerm, interestRate, propertyTax, homeInsurance, monthlyHoa, maxDti, maxHousingRatio]);

  const calculateAffordability = () => {
    const monthlyIncome = annualIncome / 12;
    
    // Calculate max monthly payment based on DTI ratio
    const maxTotalPaymentByDti = (monthlyIncome * (maxDti / 100)) - monthlyDebts;
    
    // Calculate max monthly payment based on housing ratio
    const maxTotalPaymentByHousing = monthlyIncome * (maxHousingRatio / 100);
    
    // Use the lower of the two
    const maxMonthlyPayment = Math.min(maxTotalPaymentByDti, maxTotalPaymentByHousing);
    
    // Subtract HOA from available payment
    const availableForMortgage = Math.max(0, maxMonthlyPayment - monthlyHoa);
    
    // Calculate max home price iteratively (since taxes/insurance depend on home value)
    let maxHomePrice = 0;
    let monthlyPI = 0;
    let monthlyTax = 0;
    let monthlyInsurance = 0;
    let monthlyPmi = 0;
    
    // Binary search for max home price
    let low = 0;
    let high = 10000000;
    const monthlyRate = interestRate / 100 / 12;
    const numberOfPayments = loanTerm * 12;
    
    while (high - low > 1000) {
      const mid = (low + high) / 2;
      const loanAmount = mid - downPayment;
      
      if (loanAmount <= 0) {
        low = mid;
        continue;
      }
      
      // Calculate P&I
      let pi = 0;
      if (monthlyRate > 0) {
        pi = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
             (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
      } else {
        pi = loanAmount / numberOfPayments;
      }
      
      // Calculate monthly tax and insurance (from yearly dollar amounts)
      const tax = propertyTax / 12;
      const insurance = homeInsurance / 12;
      
      // PMI if down payment < 20%
      const downPaymentPercent = (downPayment / mid) * 100;
      const pmi = downPaymentPercent < 20 ? (loanAmount * 0.007) / 12 : 0;
      
      const totalPayment = pi + tax + insurance + pmi;
      
      if (totalPayment <= availableForMortgage) {
        low = mid;
        monthlyPI = pi;
        monthlyTax = tax;
        monthlyInsurance = insurance;
        monthlyPmi = pmi;
      } else {
        high = mid;
      }
    }
    
    maxHomePrice = Math.floor(low / 1000) * 1000; // Round down to nearest $1000
    
    // Recalculate with final home price
    const finalLoanAmount = maxHomePrice - downPayment;
    if (finalLoanAmount > 0 && monthlyRate > 0) {
      monthlyPI = finalLoanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
                  (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    }
    monthlyTax = propertyTax / 12;
    monthlyInsurance = homeInsurance / 12;
    const finalDownPaymentPercent = maxHomePrice > 0 ? (downPayment / maxHomePrice) * 100 : 0;
    monthlyPmi = finalDownPaymentPercent < 20 ? (finalLoanAmount * 0.007) / 12 : 0;
    
    const totalMonthlyPayment = monthlyPI + monthlyTax + monthlyInsurance + monthlyPmi + monthlyHoa;
    
    // Calculate total interest over loan term
    const totalPayments = monthlyPI * numberOfPayments;
    const totalInterest = totalPayments - finalLoanAmount;
    
    setResults({
      maxHomePrice,
      loanAmount: finalLoanAmount,
      monthlyPI,
      monthlyTax,
      monthlyInsurance,
      monthlyPmi,
      monthlyHoa,
      totalMonthlyPayment,
      totalInterest,
      downPaymentPercent: finalDownPaymentPercent,
      maxMonthlyPayment,
      currentDti: monthlyIncome > 0 ? ((totalMonthlyPayment + monthlyDebts) / monthlyIncome) * 100 : 0,
      currentHousingRatio: monthlyIncome > 0 ? (totalMonthlyPayment / monthlyIncome) * 100 : 0
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatCurrencyDetailed = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Get percentage breakdown for visualization
  const getBreakdown = () => {
    if (!results || results.totalMonthlyPayment === 0) {
      return { pi: 60, tax: 15, ins: 10, pmi: 5, hoa: 10 };
    }
    const total = results.totalMonthlyPayment;
    return {
      pi: Math.round((results.monthlyPI / total) * 100),
      tax: Math.round((results.monthlyTax / total) * 100),
      ins: Math.round((results.monthlyInsurance / total) * 100),
      pmi: Math.round((results.monthlyPmi / total) * 100),
      hoa: Math.round((results.monthlyHoa / total) * 100)
    };
  };

  const breakdown = getBreakdown();

  // Generate PDF Report
  const generatePdf = async () => {
    setIsGeneratingPdf(true);
    
    try {
      const today = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Home Affordability Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; color: #333; max-width: 800px; margin: 0 auto; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #10b981; padding-bottom: 20px; }
            .header h1 { color: #10b981; margin: 0; font-size: 28px; }
            .header p { color: #666; margin-top: 5px; }
            .section { margin-bottom: 25px; }
            .section-title { font-size: 18px; font-weight: bold; color: #10b981; margin-bottom: 15px; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; }
            .highlight-box { background: linear-gradient(135deg, #10b981, #059669); color: white; padding: 25px; border-radius: 12px; text-align: center; margin-bottom: 25px; }
            .highlight-box .label { font-size: 14px; opacity: 0.9; }
            .highlight-box .amount { font-size: 42px; font-weight: bold; margin-top: 5px; }
            .highlight-box .sub { font-size: 16px; margin-top: 10px; opacity: 0.9; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
            .item { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #f3f4f6; }
            .item-label { color: #666; }
            .item-value { font-weight: 600; }
            .item-value.primary { color: #10b981; }
            .item-value.danger { color: #ef4444; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #999; font-size: 12px; }
            .disclaimer { background: #f9fafb; padding: 15px; border-radius: 8px; font-size: 11px; color: #666; margin-top: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Home Affordability Report</h1>
            <p>Generated on ${today}</p>
          </div>
          
          <div class="highlight-box">
            <div class="label">You Can Afford a Home Up To</div>
            <div class="amount">${formatCurrency(results?.maxHomePrice || 0)}</div>
            <div class="sub">Monthly Payment: ${formatCurrencyDetailed(results?.totalMonthlyPayment || 0)}</div>
          </div>
          
          <div class="grid">
            <div class="section">
              <div class="section-title">Your Financial Profile</div>
              <div class="item">
                <span class="item-label">Annual Income</span>
                <span class="item-value">${formatCurrency(annualIncome)}</span>
              </div>
              <div class="item">
                <span class="item-label">Monthly Debts</span>
                <span class="item-value">${formatCurrency(monthlyDebts)}</span>
              </div>
              <div class="item">
                <span class="item-label">Down Payment</span>
                <span class="item-value">${formatCurrency(downPayment)} (${results?.downPaymentPercent?.toFixed(1) || 0}%)</span>
              </div>
              <div class="item">
                <span class="item-label">Debt-to-Income Ratio</span>
                <span class="item-value">${results?.currentDti?.toFixed(1) || 0}%</span>
              </div>
              <div class="item">
                <span class="item-label">Housing Ratio</span>
                <span class="item-value">${results?.currentHousingRatio?.toFixed(1) || 0}%</span>
              </div>
            </div>
            
            <div class="section">
              <div class="section-title">Monthly Payment Breakdown</div>
              <div class="item">
                <span class="item-label">Principal & Interest</span>
                <span class="item-value">${formatCurrencyDetailed(results?.monthlyPI || 0)}</span>
              </div>
              <div class="item">
                <span class="item-label">Property Tax</span>
                <span class="item-value">${formatCurrencyDetailed(results?.monthlyTax || 0)}</span>
              </div>
              <div class="item">
                <span class="item-label">Home Insurance</span>
                <span class="item-value">${formatCurrencyDetailed(results?.monthlyInsurance || 0)}</span>
              </div>
              ${results?.monthlyPmi > 0 ? `
              <div class="item">
                <span class="item-label">PMI</span>
                <span class="item-value">${formatCurrencyDetailed(results?.monthlyPmi || 0)}</span>
              </div>
              ` : ''}
              ${results?.monthlyHoa > 0 ? `
              <div class="item">
                <span class="item-label">HOA Fee</span>
                <span class="item-value">${formatCurrencyDetailed(results?.monthlyHoa || 0)}</span>
              </div>
              ` : ''}
            </div>
          </div>
          
          <div class="section">
            <div class="section-title">Loan Details</div>
            <div class="item">
              <span class="item-label">Loan Amount</span>
              <span class="item-value primary">${formatCurrency(results?.loanAmount || 0)}</span>
            </div>
            <div class="item">
              <span class="item-label">Interest Rate</span>
              <span class="item-value">${interestRate}%</span>
            </div>
            <div class="item">
              <span class="item-label">Loan Term</span>
              <span class="item-value">${loanTerm} years</span>
            </div>
            <div class="item">
              <span class="item-label">Total Interest Over Loan Term</span>
              <span class="item-value danger">${formatCurrency(results?.totalInterest || 0)}</span>
            </div>
          </div>
          
          <div class="disclaimer">
            <strong>Disclaimer:</strong> This is an estimate only and should not be considered as pre-qualification or pre-approval. 
            Actual loan amounts and payments may vary based on your complete financial picture, credit score, and lender requirements. 
            Please consult with a mortgage professional for accurate figures.
          </div>
          
          <div class="footer">
            Generated by ListingPro AI Home Affordability Calculator
          </div>
        </body>
        </html>
      `;
      
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
      
      toast.success('Report generated! Use your browser\'s print dialog to save as PDF.');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate report');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const InfoTooltip = ({ text }) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <HelpCircle size={14} className="text-muted-foreground cursor-help ml-1" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="text-sm">{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  // Prepare calculation data for saving
  const getCalculationData = () => {
    if (!results) return null;
    return {
      annual_income: annualIncome,
      monthly_debts: monthlyDebts,
      down_payment: downPayment,
      loan_term: loanTerm,
      interest_rate: interestRate,
      property_tax: propertyTax,
      home_insurance: homeInsurance,
      hoa_fee: monthlyHoa,
      max_dti: maxDti,
      max_housing_ratio: maxHousingRatio,
      max_home_price: results.maxHomePrice,
      monthly_payment: results.totalMonthlyPayment,
      monthly_pi: results.monthlyPI,
      monthly_tax: results.monthlyTax,
      monthly_insurance: results.monthlyInsurance,
      monthly_pmi: results.monthlyPmi,
      monthly_hoa: results.monthlyHoa,
      loan_amount: results.loanAmount,
      current_dti: results.currentDti,
      current_housing_ratio: results.currentHousingRatio
    };
  };

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="affordability-calculator-page">
      <Navbar variant="app" />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground flex items-center gap-3">
                <Home className="text-green-600" size={32} />
                Home Affordability Calculator
              </h1>
              <p className="text-muted-foreground mt-1">Determine how much home you can afford</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => setShowSaveDialog(true)} 
              disabled={!results}
              className="gap-2"
              data-testid="save-affordability-btn"
            >
              <Save size={18} />
              Save & Assign
            </Button>
            <Button 
              onClick={generatePdf} 
              disabled={isGeneratingPdf || !results}
              className="gap-2 bg-green-600 hover:bg-green-700"
              data-testid="download-affordability-pdf-btn"
            >
              <Download size={18} />
              {isGeneratingPdf ? 'Generating...' : 'Download Report'}
            </Button>
          </div>
        </div>

        {/* Main Result Card */}
        <Card className="bg-gradient-to-br from-green-600 to-emerald-700 text-white mb-8">
          <CardContent className="py-8">
            <div className="text-center">
              <p className="text-white/80 text-lg mb-2">Based on your income and expenses, you can afford a home up to</p>
              <p className="text-5xl md:text-6xl font-bold mb-4" data-testid="max-home-price">
                {formatCurrency(results?.maxHomePrice || 0)}
              </p>
              <p className="text-xl text-white/90">
                with a total monthly payment of <span className="font-bold">{formatCurrencyDetailed(results?.totalMonthlyPayment || 0)}</span>
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Inputs */}
          <div className="lg:col-span-2 space-y-6">
            {/* Income & Debts */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Wallet className="text-green-600" size={20} />
                  Income & Debts
                </CardTitle>
                <CardDescription>Your financial situation helps determine what you can afford</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Annual Income */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-sm font-medium flex items-center">
                      Annual Gross Income
                      <InfoTooltip text="Your total yearly income before taxes and deductions. Include all sources of regular income." />
                    </Label>
                    <div className="relative w-40">
                      <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="number"
                        value={annualIncome}
                        onChange={(e) => setAnnualIncome(Number(e.target.value) || 0)}
                        className="pl-8 text-right font-semibold"
                        data-testid="annual-income-input"
                      />
                    </div>
                  </div>
                  <Slider
                    value={[annualIncome]}
                    onValueChange={(v) => setAnnualIncome(v[0])}
                    min={20000}
                    max={500000}
                    step={5000}
                    className="py-2"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>$20,000</span>
                    <span>$500,000</span>
                  </div>
                </div>

                {/* Monthly Debts */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-sm font-medium flex items-center">
                      Monthly Debt Payments
                      <InfoTooltip text="Include car payments, student loans, credit card minimums, and other recurring monthly debt payments." />
                    </Label>
                    <div className="relative w-40">
                      <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="number"
                        value={monthlyDebts}
                        onChange={(e) => setMonthlyDebts(Number(e.target.value) || 0)}
                        className="pl-8 text-right font-semibold"
                        data-testid="monthly-debts-input"
                      />
                    </div>
                  </div>
                  <Slider
                    value={[monthlyDebts]}
                    onValueChange={(v) => setMonthlyDebts(v[0])}
                    min={0}
                    max={5000}
                    step={50}
                    className="py-2"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>$0</span>
                    <span>$5,000</span>
                  </div>
                </div>

                {/* Down Payment */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-sm font-medium flex items-center">
                      Down Payment Available
                      <InfoTooltip text="The amount of cash you have available for a down payment. A larger down payment can help you afford more home and avoid PMI." />
                    </Label>
                    <div className="relative w-40">
                      <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="number"
                        value={downPayment}
                        onChange={(e) => setDownPayment(Number(e.target.value) || 0)}
                        className="pl-8 text-right font-semibold"
                        data-testid="down-payment-input"
                      />
                    </div>
                  </div>
                  <Slider
                    value={[downPayment]}
                    onValueChange={(v) => setDownPayment(v[0])}
                    min={0}
                    max={500000}
                    step={5000}
                    className="py-2"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>$0</span>
                    <span>$500,000</span>
                  </div>
                  {results && results.downPaymentPercent < 20 && results.maxHomePrice > 0 && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <Info size={12} />
                      Down payment is {results.downPaymentPercent.toFixed(1)}% - PMI will be required
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Loan Details */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <PiggyBank className="text-green-600" size={20} />
                  Loan Details
                </CardTitle>
                <CardDescription>Interest rate and term affect your monthly payment</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Interest Rate */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label className="text-sm font-medium flex items-center">
                      Interest Rate (APR)
                      <InfoTooltip text="The annual percentage rate for your mortgage. Check current rates with lenders for accurate estimates." />
                    </Label>
                    <span className="text-2xl font-bold text-green-600">{interestRate}%</span>
                  </div>
                  <Slider
                    value={[interestRate]}
                    onValueChange={(v) => setInterestRate(v[0])}
                    min={2}
                    max={12}
                    step={0.125}
                    className="py-2"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>2%</span>
                    <span>12%</span>
                  </div>
                </div>

                {/* Loan Term */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium flex items-center">
                    Loan Term
                    <InfoTooltip text="Shorter terms have higher monthly payments but less total interest. Longer terms have lower payments but more total interest." />
                  </Label>
                  <div className="grid grid-cols-3 gap-3">
                    {[15, 20, 30].map((term) => (
                      <Button
                        key={term}
                        variant={loanTerm === term ? "default" : "outline"}
                        onClick={() => setLoanTerm(term)}
                        className={`w-full ${loanTerm === term ? 'bg-green-600 hover:bg-green-700' : ''}`}
                      >
                        {term} Years
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Taxes, Insurance & HOA */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShieldCheck className="text-green-600" size={20} />
                  Taxes, Insurance & HOA
                </CardTitle>
                <CardDescription>These costs are added to your monthly payment</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Property Tax */}
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center">
                      Property Tax ($/year)
                      <InfoTooltip text="Annual property tax amount. Check your local tax rates or use an estimate based on similar homes in your area." />
                    </Label>
                    <div className="relative">
                      <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="number"
                        value={propertyTax}
                        onChange={(e) => setPropertyTax(Number(e.target.value) || 0)}
                        step={100}
                        min={0}
                        className="pl-8"
                        data-testid="property-tax-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">per year</p>
                  </div>

                  {/* Home Insurance */}
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center">
                      Home Insurance ($/year)
                      <InfoTooltip text="Annual homeowners insurance premium. Get quotes from insurance providers for accurate estimates." />
                    </Label>
                    <div className="relative">
                      <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="number"
                        value={homeInsurance}
                        onChange={(e) => setHomeInsurance(Number(e.target.value) || 0)}
                        step={100}
                        min={0}
                        className="pl-8"
                        data-testid="home-insurance-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">per year</p>
                  </div>

                  {/* Monthly HOA */}
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center">
                      <Building2 size={14} className="mr-1 text-muted-foreground" />
                      Monthly HOA Fee
                      <InfoTooltip text="Homeowners Association fees if applicable. Common in condos and planned communities." />
                    </Label>
                    <div className="relative">
                      <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="number"
                        value={monthlyHoa}
                        onChange={(e) => setMonthlyHoa(Number(e.target.value) || 0)}
                        step={25}
                        min={0}
                        className="pl-8"
                        data-testid="monthly-hoa-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">per month</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Assumptions */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calculator className="text-green-600" size={20} />
                  Lending Guidelines
                </CardTitle>
                <CardDescription>These ratios determine your maximum affordable payment</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Max DTI */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <Label className="text-sm font-medium flex items-center">
                        Max Debt-to-Income Ratio
                        <InfoTooltip text="The maximum percentage of your gross monthly income that can go toward all debt payments (including the new mortgage). Most lenders prefer 43% or less." />
                      </Label>
                      <span className="text-lg font-bold text-green-600">{maxDti}%</span>
                    </div>
                    <Slider
                      value={[maxDti]}
                      onValueChange={(v) => setMaxDti(v[0])}
                      min={20}
                      max={50}
                      step={1}
                      className="py-2"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>20% (Conservative)</span>
                      <span>50% (Aggressive)</span>
                    </div>
                  </div>

                  {/* Max Housing Ratio */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <Label className="text-sm font-medium flex items-center">
                        Max Housing Ratio
                        <InfoTooltip text="The maximum percentage of your gross monthly income that should go toward housing costs alone. Most lenders prefer 28% or less." />
                      </Label>
                      <span className="text-lg font-bold text-green-600">{maxHousingRatio}%</span>
                    </div>
                    <Slider
                      value={[maxHousingRatio]}
                      onValueChange={(v) => setMaxHousingRatio(v[0])}
                      min={15}
                      max={40}
                      step={1}
                      className="py-2"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>15% (Conservative)</span>
                      <span>40% (Aggressive)</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Results */}
          <div className="space-y-6">
            {/* Monthly Payment Breakdown */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Monthly Payment Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Principal & Interest</span>
                  <span className="font-semibold">{formatCurrencyDetailed(results?.monthlyPI || 0)}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Property Tax</span>
                  <span className="font-semibold">{formatCurrencyDetailed(results?.monthlyTax || 0)}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Home Insurance</span>
                  <span className="font-semibold">{formatCurrencyDetailed(results?.monthlyInsurance || 0)}</span>
                </div>
                {results?.monthlyPmi > 0 && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">PMI</span>
                    <span className="font-semibold">{formatCurrencyDetailed(results.monthlyPmi)}</span>
                  </div>
                )}
                {results?.monthlyHoa > 0 && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">HOA Fee</span>
                    <span className="font-semibold">{formatCurrencyDetailed(results.monthlyHoa)}</span>
                  </div>
                )}
                <div className="flex justify-between py-2 bg-green-50 dark:bg-green-900/20 rounded-lg px-3 -mx-3">
                  <span className="font-semibold text-green-700 dark:text-green-400">Total Monthly</span>
                  <span className="font-bold text-green-700 dark:text-green-400">{formatCurrencyDetailed(results?.totalMonthlyPayment || 0)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Loan Summary */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Loan Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Max Home Price</span>
                  <span className="font-semibold text-green-600">{formatCurrency(results?.maxHomePrice || 0)}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Down Payment</span>
                  <span className="font-semibold">{formatCurrency(downPayment)}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Loan Amount</span>
                  <span className="font-semibold">{formatCurrency(results?.loanAmount || 0)}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Total Interest</span>
                  <span className="font-semibold text-red-500">{formatCurrency(results?.totalInterest || 0)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Your Ratios */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Your Ratios</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-muted-foreground">Debt-to-Income</span>
                    <span className="text-sm font-medium">{results?.currentDti?.toFixed(1) || 0}% / {maxDti}%</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${results?.currentDti > maxDti ? 'bg-red-500' : 'bg-green-500'}`}
                      style={{ width: `${Math.min((results?.currentDti || 0) / maxDti * 100, 100)}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-muted-foreground">Housing Ratio</span>
                    <span className="text-sm font-medium">{results?.currentHousingRatio?.toFixed(1) || 0}% / {maxHousingRatio}%</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${results?.currentHousingRatio > maxHousingRatio ? 'bg-red-500' : 'bg-green-500'}`}
                      style={{ width: `${Math.min((results?.currentHousingRatio || 0) / maxHousingRatio * 100, 100)}%` }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Breakdown Visual */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Payment Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded bg-green-600"></div>
                    <span className="text-sm flex-1">Principal & Interest</span>
                    <span className="text-sm font-medium">{breakdown.pi}%</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded bg-blue-500"></div>
                    <span className="text-sm flex-1">Property Tax</span>
                    <span className="text-sm font-medium">{breakdown.tax}%</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded bg-cyan-500"></div>
                    <span className="text-sm flex-1">Home Insurance</span>
                    <span className="text-sm font-medium">{breakdown.ins}%</span>
                  </div>
                  {breakdown.pmi > 0 && (
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded bg-amber-500"></div>
                      <span className="text-sm flex-1">PMI</span>
                      <span className="text-sm font-medium">{breakdown.pmi}%</span>
                    </div>
                  )}
                  {breakdown.hoa > 0 && (
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded bg-purple-500"></div>
                      <span className="text-sm flex-1">HOA</span>
                      <span className="text-sm font-medium">{breakdown.hoa}%</span>
                    </div>
                  )}
                </div>
                
                {/* Bar visualization */}
                <div className="mt-4 h-4 rounded-full overflow-hidden flex">
                  <div className="bg-green-600" style={{ width: `${breakdown.pi}%` }}></div>
                  <div className="bg-blue-500" style={{ width: `${breakdown.tax}%` }}></div>
                  <div className="bg-cyan-500" style={{ width: `${breakdown.ins}%` }}></div>
                  {breakdown.pmi > 0 && (
                    <div className="bg-amber-500" style={{ width: `${breakdown.pmi}%` }}></div>
                  )}
                  {breakdown.hoa > 0 && (
                    <div className="bg-purple-500" style={{ width: `${breakdown.hoa}%` }}></div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Save Calculation Dialog */}
      <SaveCalculationDialog
        open={showSaveDialog}
        onOpenChange={setShowSaveDialog}
        calculatorType="affordability"
        calculationData={getCalculationData()}
        onSaved={() => toast.success('You can now view this calculation on the listing page')}
      />
    </div>
  );
};

export default AffordabilityCalculator;
